export type Locale = 'en' | 'es';

const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Header
    'site.name': 'UNBIASED NEWS',
    'site.tagline': 'Structural Impact, Zero Noise',
    'nav.latest': 'Latest',
    'nav.admin': 'Admin',
    'nav.subscribe': 'Subscribe',
    'header.global': 'Global Impact News',
    'header.updated': 'Updated',
    'header.zeroNoise': 'Zero Noise',

    // Categories
    'cat.all': 'All Categories',
    'cat.Geopolitics': 'Geopolitics',
    'cat.Economy': 'Economy',
    'cat.Climate': 'Climate',
    'cat.Health': 'Health',
    'cat.Technology': 'Technology',
    'cat.Disasters': 'Disasters',
    'cat.Migration': 'Migration',

    // Category full names
    'cat.full.Geopolitics': 'Geopolitics & International Politics',
    'cat.full.Economy': 'Global Economy & Finance',
    'cat.full.Climate': 'Environment & Climate',
    'cat.full.Technology': 'Technology & Applied Science',

    // Category descriptions
    'cat.desc.Geopolitics': 'Global political developments, diplomatic relations, and international conflicts affecting multiple nations.',
    'cat.desc.Economy': 'Economic developments, market movements, and financial policies with worldwide implications.',
    'cat.desc.Climate': 'Environmental changes, climate policies, and sustainability initiatives affecting our planet.',
    'cat.desc.Technology': 'Technological advances and scientific discoveries with significant societal implications.',

    // Home page
    'home.title': 'Global Impact News',
    'home.subtitle': 'AI-curated news focused on structural impact with zero noise. Every story is analyzed for bias and scored for global relevance.',
    'home.storiesCount': 'Stories Today',
    'home.biasAccuracy': 'Bias Accuracy',
    'home.coverage': 'Coverage',
    'home.refresh': 'Refresh',
    'home.loading': 'Loading...',
    'home.sortBy': 'Sort by:',
    'home.sortLatest': 'Latest',
    'home.sortImpact': 'Impact',
    'home.featured': 'Featured Story',
    'home.latestImpact': 'Latest Impact News',
    'home.noArticles': 'No articles found',
    'home.noArticlesDesc': 'Try changing your filters or check back later for new content.',
    'home.errorTitle': 'Unable to load news',
    'home.errorDesc': 'Please check your connection and try again.',
    'home.tryAgain': 'Try Again',
    'home.newsRefreshed': 'News refreshed',

    // CTA
    'cta.title': 'Stay Ahead of Global Developments',
    'cta.desc': 'Register for deep analysis access and contribute to sustainable impact with every article you read.',
    'cta.button': 'Get Deep Analysis Access',

    // Article page
    'article.share': 'Share',
    'article.neutralSummary': 'Neutral Summary',
    'article.synthesized': 'Synthesized from multiple international sources by Unbiased News AI.',
    'article.deepAnalysis': 'Deep Analysis',
    'article.registerPrompt': 'Register to read our in-depth analysis with progressive vs conservative framing, blind spots in media coverage, and critical questions.',
    'article.registerButton': 'Register to Read — Free',
    'article.sourcesConsulted': 'Sources consulted:',
    'article.readOriginal': 'Read original source',
    'article.originalContent': 'This analysis is original content by Unbiased News, not a reproduction of any source material.',
    'article.notFound': 'Article not found',
    'article.notFoundDesc': 'The article you\'re looking for doesn\'t exist or has been removed.',
    'article.storyMetrics': 'Story Metrics',
    'article.impactAnalysis': 'Impact Analysis',
    'article.impact': 'Impact',
    'article.globalReach': 'Global Reach',
    'article.duration': 'Duration',
    'article.total': 'Total',
    'article.aboutAnalysis': 'About This Analysis',
    'article.aboutDesc1': 'This article is an original analysis by Unbiased News, not a copy of any single source.',
    'article.aboutDesc2': 'We review coverage from multiple international outlets across the political spectrum and synthesize a balanced perspective.',
    'article.aboutDesc3': 'Our AI identifies how progressive and conservative media frame each story differently, so you can see the full picture.',

    // News card
    'card.lowBias': 'Low Bias',
    'card.medBias': 'Medium Bias',
    'card.highBias': 'High Bias',
    'card.highImpact': 'High Impact',
    'card.medImpact': 'Medium Impact',
    'card.lowImpact': 'Low Impact',
    'card.score': 'Score',

    // Category page
    'catpage.articles': 'Articles',
    'catpage.totalImpact': 'Total Impact',
    'catpage.avgBias': 'Avg Bias Score',
    'catpage.highImpactStories': 'High Impact Stories',
    'catpage.moreStories': 'More Stories',
    'catpage.latestStories': 'Latest Stories',
    'catpage.noArticles': 'No articles yet in',
    'catpage.noArticlesDesc': 'Check back later for high-impact news in this category. Our AI is continuously monitoring global sources for relevant developments.',
    'catpage.insights': 'Insights',
    'catpage.lowBiasArticles': 'Low Bias Articles',
    'catpage.uniqueSources': 'Unique Sources',
    'catpage.errorTitle': 'Unable to load category news',
    'catpage.errorDesc': 'Please try again later.',

    // Footer
    'footer.desc': 'Delivering structural impact news with zero noise. Every article is AI-curated for global relevance and analyzed for bias to ensure you receive only the most significant developments affecting our world.',
    'footer.globalCoverage': 'Global Coverage',
    'footer.biasAnalyzed': 'Bias-Analyzed',
    'footer.impactScored': 'Impact-Scored',
    'footer.navigate': 'Navigate',
    'footer.latestNews': 'Latest News',
    'footer.geopolitics': 'Geopolitics',
    'footer.economy': 'Global Economy',
    'footer.environment': 'Environment',
    'footer.technology': 'Technology',
    'footer.impact': 'Impact',
    'footer.howWeScore': 'How We Score Impact',
    'footer.biasMethod': 'Bias Detection Method',
    'footer.ethixStaking': 'ETHIX Staking Program',
    'footer.sustainableImpact': 'Sustainable Impact',
    'footer.copyright': '© 2026 Unbiased News. Structural impact, zero noise.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.editorial': 'Editorial Standards',
    'footer.analyzed': 'Articles Analyzed',
    'footer.ethixStaked': 'ETHIX Staked for Impact',
    'footer.biasRate': 'Bias Accuracy Rate',
    'footer.reach': 'Coverage Reach',

    // Registration modal
    'register.title': 'Register to continue reading',
    'register.desc': 'Sign up to read all our in-depth analyses and receive a daily news summary in your email.',
    'register.placeholder': 'Enter your email',
    'register.submit': 'Register',
    'register.cancel': 'Cancel',
    'register.privacy': 'We respect your privacy. Unsubscribe at any time.',
    'register.success': 'Registration successful! You can now read all analyses.',

    // Newsletter modal
    'newsletter.title': 'Daily Impact Digest',
    'newsletter.desc': 'Get the most significant global developments delivered to your inbox every morning.',
    'newsletter.placeholder': 'your@email.com',
    'newsletter.submit': 'Subscribe',
    'newsletter.privacy': 'No spam. Unsubscribe anytime. We only send high-impact news.',

    // Language
    'lang.switch': 'ES',
    'lang.label': 'Español',
  },

  es: {
    // Header
    'site.name': 'UNBIASED NEWS',
    'site.tagline': 'Impacto Estructural, Cero Ruido',
    'nav.latest': 'Últimas',
    'nav.admin': 'Admin',
    'nav.subscribe': 'Suscribirse',
    'header.global': 'Noticias de Impacto Global',
    'header.updated': 'Actualizado',
    'header.zeroNoise': 'Cero Ruido',

    // Categories
    'cat.all': 'Todas las Categorías',
    'cat.Geopolitics': 'Geopolítica',
    'cat.Economy': 'Economía',
    'cat.Climate': 'Clima',
    'cat.Health': 'Salud',
    'cat.Technology': 'Tecnología',
    'cat.Disasters': 'Desastres',
    'cat.Migration': 'Migración',

    // Category full names
    'cat.full.Geopolitics': 'Geopolítica y Política Internacional',
    'cat.full.Economy': 'Economía y Finanzas Globales',
    'cat.full.Climate': 'Medio Ambiente y Clima',
    'cat.full.Technology': 'Tecnología y Ciencia Aplicada',

    // Category descriptions
    'cat.desc.Geopolitics': 'Desarrollos políticos globales, relaciones diplomáticas y conflictos internacionales que afectan a múltiples naciones.',
    'cat.desc.Economy': 'Desarrollos económicos, movimientos de mercado y políticas financieras con implicaciones mundiales.',
    'cat.desc.Climate': 'Cambios ambientales, políticas climáticas e iniciativas de sostenibilidad que afectan nuestro planeta.',
    'cat.desc.Technology': 'Avances tecnológicos y descubrimientos científicos con implicaciones sociales significativas.',

    // Home page
    'home.title': 'Noticias de Impacto Global',
    'home.subtitle': 'Noticias curadas por IA enfocadas en impacto estructural sin ruido. Cada historia es analizada por sesgo y puntuada por relevancia global.',
    'home.storiesCount': 'Noticias Hoy',
    'home.biasAccuracy': 'Precisión de Sesgo',
    'home.coverage': 'Cobertura',
    'home.refresh': 'Actualizar',
    'home.loading': 'Cargando...',
    'home.sortBy': 'Ordenar por:',
    'home.sortLatest': 'Recientes',
    'home.sortImpact': 'Impacto',
    'home.featured': 'Noticia Destacada',
    'home.latestImpact': 'Últimas Noticias de Impacto',
    'home.noArticles': 'No se encontraron artículos',
    'home.noArticlesDesc': 'Intenta cambiar los filtros o vuelve más tarde para nuevo contenido.',
    'home.errorTitle': 'No se pudieron cargar las noticias',
    'home.errorDesc': 'Verifica tu conexión e intenta de nuevo.',
    'home.tryAgain': 'Intentar de nuevo',
    'home.newsRefreshed': 'Noticias actualizadas',

    // CTA
    'cta.title': 'Mantente al Día con los Desarrollos Globales',
    'cta.desc': 'Regístrate para acceder al análisis profundo y contribuye al impacto sostenible con cada artículo que leas.',
    'cta.button': 'Acceder al Análisis Profundo',

    // Article page
    'article.share': 'Compartir',
    'article.neutralSummary': 'Resumen Neutral',
    'article.synthesized': 'Sintetizado de múltiples fuentes internacionales por la IA de Unbiased News.',
    'article.deepAnalysis': 'Análisis Profundo',
    'article.registerPrompt': 'Regístrate para leer nuestro análisis en profundidad con marcos progresistas vs conservadores, puntos ciegos en la cobertura mediática y preguntas clave.',
    'article.registerButton': 'Regístrate para Leer — Gratis',
    'article.sourcesConsulted': 'Fuentes consultadas:',
    'article.readOriginal': 'Leer fuente original',
    'article.originalContent': 'Este análisis es contenido original de Unbiased News, no una reproducción de ningún material fuente.',
    'article.notFound': 'Artículo no encontrado',
    'article.notFoundDesc': 'El artículo que buscas no existe o ha sido eliminado.',
    'article.storyMetrics': 'Métricas de la Noticia',
    'article.impactAnalysis': 'Análisis de Impacto',
    'article.impact': 'Impacto',
    'article.globalReach': 'Alcance Global',
    'article.duration': 'Duración',
    'article.total': 'Total',
    'article.aboutAnalysis': 'Sobre Este Análisis',
    'article.aboutDesc1': 'Este artículo es un análisis original de Unbiased News, no una copia de ninguna fuente individual.',
    'article.aboutDesc2': 'Revisamos la cobertura de múltiples medios internacionales del espectro político y sintetizamos una perspectiva equilibrada.',
    'article.aboutDesc3': 'Nuestra IA identifica cómo los medios progresistas y conservadores enmarcan cada historia de manera diferente, para que puedas ver el panorama completo.',

    // News card
    'card.lowBias': 'Sesgo Bajo',
    'card.medBias': 'Sesgo Medio',
    'card.highBias': 'Sesgo Alto',
    'card.highImpact': 'Alto Impacto',
    'card.medImpact': 'Impacto Medio',
    'card.lowImpact': 'Bajo Impacto',
    'card.score': 'Puntuación',

    // Category page
    'catpage.articles': 'Artículos',
    'catpage.totalImpact': 'Impacto Total',
    'catpage.avgBias': 'Sesgo Promedio',
    'catpage.highImpactStories': 'Noticias de Alto Impacto',
    'catpage.moreStories': 'Más Noticias',
    'catpage.latestStories': 'Últimas Noticias',
    'catpage.noArticles': 'Aún no hay artículos en',
    'catpage.noArticlesDesc': 'Vuelve más tarde para noticias de alto impacto en esta categoría. Nuestra IA monitorea continuamente fuentes globales para desarrollos relevantes.',
    'catpage.insights': 'Datos',
    'catpage.lowBiasArticles': 'Artículos de Sesgo Bajo',
    'catpage.uniqueSources': 'Fuentes Únicas',
    'catpage.errorTitle': 'No se pudieron cargar las noticias de la categoría',
    'catpage.errorDesc': 'Intenta de nuevo más tarde.',

    // Footer
    'footer.desc': 'Noticias de impacto estructural sin ruido. Cada artículo es curado por IA para relevancia global y analizado por sesgo para que recibas solo los desarrollos más significativos que afectan nuestro mundo.',
    'footer.globalCoverage': 'Cobertura Global',
    'footer.biasAnalyzed': 'Sesgo Analizado',
    'footer.impactScored': 'Impacto Puntuado',
    'footer.navigate': 'Navegar',
    'footer.latestNews': 'Últimas Noticias',
    'footer.geopolitics': 'Geopolítica',
    'footer.economy': 'Economía Global',
    'footer.environment': 'Medio Ambiente',
    'footer.technology': 'Tecnología',
    'footer.impact': 'Impacto',
    'footer.howWeScore': 'Cómo Puntuamos el Impacto',
    'footer.biasMethod': 'Método de Detección de Sesgo',
    'footer.ethixStaking': 'Programa de Staking ETHIX',
    'footer.sustainableImpact': 'Impacto Sostenible',
    'footer.copyright': '© 2026 Unbiased News. Impacto estructural, cero ruido.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',
    'footer.editorial': 'Estándares Editoriales',
    'footer.analyzed': 'Artículos Analizados',
    'footer.ethixStaked': 'ETHIX en Staking por Impacto',
    'footer.biasRate': 'Precisión de Sesgo',
    'footer.reach': 'Alcance de Cobertura',

    // Registration modal
    'register.title': 'Regístrate para seguir leyendo',
    'register.desc': 'Regístrate para leer todos nuestros análisis y recibir un resumen diario de noticias en tu email.',
    'register.placeholder': 'Ingresa tu email',
    'register.submit': 'Registrarse',
    'register.cancel': 'Cancelar',
    'register.privacy': 'Respetamos tu privacidad. Cancela cuando quieras.',
    'register.success': '¡Registro exitoso! Ya puedes leer todos los análisis.',

    // Newsletter modal
    'newsletter.title': 'Resumen Diario de Impacto',
    'newsletter.desc': 'Recibe los desarrollos globales más significativos en tu bandeja de entrada cada mañana.',
    'newsletter.placeholder': 'tu@email.com',
    'newsletter.submit': 'Suscribirse',
    'newsletter.privacy': 'Sin spam. Cancela cuando quieras. Solo enviamos noticias de alto impacto.',

    // Language
    'lang.switch': 'EN',
    'lang.label': 'English',
  }
};

export default translations;